import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Element } from '../../equipment/models/elements/element';
import { Figure } from '../../equipment/models/figures/figure';
import { ProductGroup } from '../models/product-group';
import { HighlightOptionsValue } from '../../core/services/highlight-products.service';
import { Block } from '../models/block';
import { Utils } from '../../core/services/utils.service';
import { IPlanogram } from '../models/planogram';
import { EquipmentElements } from '../../equipment/models/elements/equipmentElements';
import { POSMaterial } from '../models/POSMaterials/POSMaterial';
import { LayoutBlock } from '../planogram-edit/components/layout-rules/layout-rules.component';
import { ShelfElement } from '../models/elements/shelfElement';
import { PalletElement } from '../models/elements/palletElement';
import { EquipmentBase } from '../models/equipment-base';
import { HangBarElement } from '../models/elements/hangBarElement';
import { Product } from '../models/product';
import { ExtensionType } from '../../equipment/models/elements/extension';
import { DndDropEvent } from 'ngx-drag-drop';
import { PlanogramViewBehavior } from '../models/planogramViewBehavior';
import { Point } from '../../core/models/point';
import { ICanPutProductsResult } from '../models/canPutProductsResult';
import { BoxElement } from '../models/elements/boxElement';
import { SessionService } from '../../core/services/session.service';
import { SnackBarService } from '../../core/services/snack-bar.service';
import { MixpanelService } from '../../core/services/mixpanel.service';
import { MixpanelEvents } from '../../core/models/mixpanelEvents';
import { POSImageMaterial } from '../models/POSMaterials/POSImageMaterial';
import { POSTextMaterial } from '../models/POSMaterials/POSTextMaterial';
import { filter, Observable, Subject, take } from 'rxjs';
import { HookElement } from '../models/elements/hookElement';
import { HoleElement } from '../models/elements/holeElement';
import { Rectangle } from '../../equipment/models/figures/rectangle';
import { AlignmentTypes } from '../models/alignment';
import { HistoryInstance } from '../models/history-instance';
import { PlanogramCanvasLayer } from '../models/planogram-canvas-layer';

export const NORMALIZED_ELEMENTS = [
	'ShelfElement',
	'HangBarElement',
	'BoxElement',
	'HookElement',
	'PalletElement',
	'HoleElement'
];

const CAN_CHANGE_PRODUCT_POSITION: Array<EquipmentElements> = [
	EquipmentElements.BaseRectangle,
	EquipmentElements.BaseTrapeze,
	EquipmentElements.Shelf,
	EquipmentElements.AngleShelf,
	EquipmentElements.Box,
	EquipmentElements.SlicedBox,
	EquipmentElements.Hook,
	EquipmentElements.Hole
];

export interface ILabelsState {
	abcReceipts: boolean;
	abcTurnover: boolean;
	dateFrom: string;
	dateTo: string;
	price: boolean;
	promoProducts: boolean;
	newProducts: boolean;
	articles: boolean;
	blocks: boolean;
	stockQty: boolean;
	allShops: boolean;
}

export interface DetailedVariables {
	detailedElementNumber: number;
	detailedElementNext: Element;
	detailedElementPrev: Element;
}

export interface CanvasUpdateRequest {
	stands?: Array<IPlanogram>;
	ids?: Array<string>;
	reason?: string;
	requestId?: string;
	layers?: Array<PlanogramCanvasLayer>;
}

export interface CanvasDropRequest {
	planogram: IPlanogram;
	event: DndDropEvent;
}

export interface SortProductsOption {
	name: string;
	by: string;
	type: 'asc' | 'desc';
}

interface ISettingsPanel {
	emptyProducts: boolean;
	emptyBlocks: boolean;
	emptyLabels: boolean;
	tooManyLabels: boolean;
	differentElements: boolean;
	looseProduct: boolean;
}

export interface IPlanogramHistoryState {
	entries: Array<HistoryInstance>;
	index: number;
	savedIndex: number;
	beginState: HistoryInstance;
}

const EMPTY_HISTORY: IPlanogramHistoryState = { entries: [], index: 0, savedIndex: 0, beginState: null };
const MAX_HISTORY_ENTRIES = 20;

@Injectable()
export class PlanogramActivity {

	private readonly _planogram: WritableSignal<IPlanogram> = signal<IPlanogram>(null);
	private readonly _allPlanograms: WritableSignal<Array<IPlanogram>> = signal([]);
	private readonly _histories = signal<Record<number, IPlanogramHistoryState>>({});
	private readonly _historyApplying = signal<boolean>(false);

	private readonly _activeBlocks = signal<Array<Block>>([]);
	private readonly _activePosMaterials = signal<Array<POSMaterial>>([]);
	private readonly _activePanelProducts = signal<Array<Product>>([]);
	private readonly _productsGrouping = signal<string>('');
	private readonly _activeProducts = signal<Array<ProductGroup>>([]);
	private readonly _dragProducts = signal<Array<Product>>([]);
	private readonly _editMode = signal<boolean>(false);
	private readonly _filters = signal<{ interval: number, metrics: Array<string> }>({ interval: 1, metrics: [] });
	private readonly _filterType = signal<string>('default');
	private readonly _focusedCanvas = signal<{ planogram: number, element: string, canvas: HTMLDivElement }>({ planogram: null, element: null, canvas: null });
	private readonly _highlightValue = signal<HighlightOptionsValue>(HighlightOptionsValue.Empty);
	private readonly _highlightedProducts = signal<{ [key: number]: any }>({});
	private readonly _labelsState = signal<ILabelsState>({
		stockQty: false,
		price: false,
		promoProducts: false,
		newProducts: false,
		articles: false,
		blocks: true,
		abcReceipts: false,
		abcTurnover: false,
		allShops: false,
		dateTo: null,
		dateFrom: null
	});
	private readonly _layoutRulesProducts = signal<any>({});
	private readonly _panelType = signal<string>('');
	private readonly _productMove = signal<boolean>(false);
	private readonly _rotatedProducts = signal<{ [key: number]: { color: string } }>({});
	private readonly _selectedBlock = signal<Block>(null);
	private readonly _selectedElement = signal<string>('');
	private readonly _settingsPanel = signal<ISettingsPanel>({
		emptyProducts: true,
		emptyBlocks: true,
		emptyLabels: true,
		tooManyLabels: false,
		differentElements: false,
		looseProduct: false
	});
	private readonly _standData = signal<{ [key: number]: any }>({});
	private readonly _targetElement = signal<Element | null>(null);

    public readonly planogram = this._planogram.asReadonly();
    public readonly allPlanograms = this._allPlanograms.asReadonly();
	public readonly activeBlocks = this._activeBlocks.asReadonly();
	public readonly activePosMaterials = this._activePosMaterials.asReadonly();
	public readonly activePanelProducts = this._activePanelProducts.asReadonly();
	public readonly productsGrouping = this._productsGrouping.asReadonly();
	public readonly activeProducts = this._activeProducts.asReadonly();
	public readonly blockForRule = computed(() => {
		const block: Block = this._activeBlocks()[0] ?? this._selectedBlock();
		const blockForRule: LayoutBlock = {
			id: null,
			start: 0,
			end: this.planogram().stand.Width,
			shelves: [],
			width: this.planogram().stand.Width
		};

		if (block) {
			blockForRule.id = block.localId;
			blockForRule.start = Math.max(block.begin[0].x, 0);
			blockForRule.end = Math.min(Utils.round(block.begin[0].x + block.width), block.stand.Width);
			blockForRule.shelves = Utils.unique(
				block.elements
					.filter((el: Element) => el.is(ShelfElement, PalletElement))
					.map((el: Element) => Utils.getElementIndex(el, block.stand).toString())
			);
			blockForRule.width = Utils.round(blockForRule.end - blockForRule.start);
		}

		if (!Utils.equalObjects(blockForRule, this.blockForRule)) return blockForRule;
	});
	public readonly canChangeProductPosition = computed(() => {
		return this.canChangeProductPositionFor(this._activeProducts());
	});
	public readonly dragProducts = this._dragProducts.asReadonly();
	public readonly editMode = this._editMode.asReadonly();
	public readonly filters = this._filters.asReadonly();
	public readonly filterType = this._filterType.asReadonly();
	public readonly focusedCanvas = this._focusedCanvas.asReadonly();
	public readonly highlightValue = this._highlightValue.asReadonly();
	public readonly highlightActive = computed(() => this._highlightValue() != HighlightOptionsValue.Empty);
	public readonly highlightedProducts = this._highlightedProducts.asReadonly();
	public readonly editLabelsActive = computed(() => {
		const state: ILabelsState = this._labelsState();
		return state.abcTurnover || state.abcReceipts || state.price || state.stockQty;
	});
	public readonly viewLabelsActive = computed(() => {
		const state: ILabelsState = this._labelsState();
		return state.price || state.promoProducts || state.newProducts || state.articles || state.blocks;
	});
	public readonly labelsState = this._labelsState.asReadonly();
	public readonly layoutRulesProducts = this._layoutRulesProducts.asReadonly();
	public readonly panelBlock = computed(() => this._activeBlocks().length ? this._activeBlocks()[0].copy() : null);
	public readonly panelOpened = computed(() => this._panelType() != '');
	public readonly panelPOSMaterial = computed(() => this._activePosMaterials()[0] ?? null);
	public readonly panelType = this._panelType.asReadonly();
	public readonly previewProduct: Signal<Product> = computed(() => {
		const products: Array<Product> = this._activePanelProducts();
		if (!products.length) return null;
		const productId: number = products[0].id;
		return products.every((product: Product) => product.id === productId) ? products[0] : null;
	});
	public readonly previewProductStatus = computed(() => this.previewProduct() ? 'active' : 'inactive');
	public readonly productMove = this._productMove.asReadonly();
	public readonly rotatedProducts = this._rotatedProducts.asReadonly();
	public readonly selectedBlock = this._selectedBlock.asReadonly();
	public readonly selectedElement = this._selectedElement.asReadonly();
	public readonly settingsPanel = this._settingsPanel.asReadonly();
	public readonly settingsPanelDisabled = computed(() => {
		const settings: ISettingsPanel = this._settingsPanel();
		const empty: boolean = settings.emptyProducts && settings.emptyBlocks && settings.emptyLabels;
		return empty || settings.differentElements || settings.looseProduct || settings.tooManyLabels;
	});
	public readonly sortingActive = computed(() => {
		return this.isSortingActiveFor(this.planogram(), this._activeProducts(), this._activeBlocks());
	});
	public readonly standData = this._standData.asReadonly();
	public readonly targetElement = this._targetElement.asReadonly();
	public readonly historyApplying = this._historyApplying.asReadonly();
	public readonly activePlanogramId = computed(() => this._planogram()?.main_stand ?? null);
	public readonly activeHistory = computed((): IPlanogramHistoryState => {
		const id: number = this.activePlanogramId();
		if (!id) return EMPTY_HISTORY;
		return this._histories()[id] ?? EMPTY_HISTORY;
	});
	public readonly historyEntries = computed(() => this.activeHistory().entries);
	public readonly historyIndex = computed(() => this.activeHistory().index);
	public readonly beginState = computed(() => this.activeHistory().beginState);
	public readonly hasAnyHistoryChanges = computed((): boolean =>
		Object.values(this._histories()).some((state: IPlanogramHistoryState) => state.index !== state.savedIndex)
	);

	public buildProductsTable$: Subject<boolean> = new Subject<boolean>();
	public highlightUpdate$: Subject<void> = new Subject<void>();
	public historyGenerate$: Subject<number | void> = new Subject<number | void>();
	public setRealProductPercents$: Subject<void> = new Subject<void>();
	public planogramUpdate$: Subject<IPlanogram> = new Subject<IPlanogram>();
	public rebuildProductsTable$: Subject<void> = new Subject<void>();
	public reloadABCReport$: Subject<void> = new Subject<void>();
	public reloadEfficiencyAnalysis$: Subject<void> = new Subject<void>();
	public setNumbersForPerforation$: Subject<Array<Element>> = new Subject<Array<Element>>();
	public showDeleteMessage$: Subject<{ block: Block, errors: Array<ProductGroup> }> = new Subject<{ block: Block, errors: Array<ProductGroup> }>();
	public canvasUpdate$: Subject<CanvasUpdateRequest> = new Subject<CanvasUpdateRequest>();
	public canvasUpdateCompleted$: Subject<CanvasUpdateRequest> = new Subject<CanvasUpdateRequest>();
	public canvasDrop$: Subject<CanvasDropRequest> = new Subject<CanvasDropRequest>();

	constructor(
		private sessionService: SessionService,
		private snackBarService: SnackBarService,
		private mixPanelService: MixpanelService
	) {}

    public setPlanogram(value: IPlanogram): void {
        this._planogram.set(value);
    }

	public setAllPlanograms(planograms: Array<IPlanogram>): void {
		this._allPlanograms.set(planograms);
	}

	public getPlanogramsWithSameEquipment(equipmentId: number, excludePlanogram?: IPlanogram): Array<IPlanogram> {
		return this._allPlanograms().filter((planogram: IPlanogram) =>
			planogram.stand.id === equipmentId && planogram !== excludePlanogram
		);
	}

	public syncEquipmentStandFromSource(sourcePlanogram: IPlanogram, targetPlanogram: IPlanogram): void {
		if (sourcePlanogram.stand.id !== targetPlanogram.stand.id) return;

		const sourceStand: EquipmentBase = sourcePlanogram.stand;
		const targetStand: EquipmentBase = targetPlanogram.stand;

		sourceStand.allElements.forEach((sourceEl: Element) => {
			const targetEl: Element | undefined = targetStand.getElementById(sourceEl.id);
			if (!targetEl) return;

			const oldBegin: Point = { ...targetEl.shapes[0].begin };
			const newBegin: Point = { ...sourceEl.shapes[0].begin };

			this.applyShapeBegin(targetEl.shapes[0], newBegin);

			if (targetEl.parent.is(EquipmentElements.AngleShelf)) {
				(targetEl.shapes[0] as Rectangle).updateRotationPoint(targetStand, targetEl);
			}

			const dx: number = Utils.round(newBegin.x - oldBegin.x);
			const dy: number = Utils.round(newBegin.y - oldBegin.y);
			const dz: number = Utils.round(newBegin.z - oldBegin.z);

			if (dx || dy || dz) {
				targetStand.products
					.filter((pg: ProductGroup) => pg.element.id === targetEl.id)
					.forEach((pg: ProductGroup) => pg.move(dx, dy, dz));
			}
		});

		targetStand.allElements.forEach((el: Element) => {
			if (NORMALIZED_ELEMENTS.includes(el.constructor.name)) el.normalize(targetStand);
		});
		targetStand.blocks.forEach((block: Block) => block.clearCache());
	}

	private applyShapeBegin(shape: Figure, newBegin: Point): void {
		const dx: number = Utils.round(newBegin.x - shape.begin.x);
		const dy: number = Utils.round(newBegin.y - shape.begin.y);
		const dz: number = Utils.round(newBegin.z - shape.begin.z);

		if (dx || dy || dz) {
			shape.drag(dx, dy, dz);
			return;
		}

		shape.update({});
	}

	public syncSharedEquipmentStands(sourcePlanogram: IPlanogram): Array<IPlanogram> {
		const siblings: Array<IPlanogram> = this.getPlanogramsWithSameEquipment(
			sourcePlanogram.stand.id,
			sourcePlanogram
		);

		siblings.forEach((planogram: IPlanogram) => this.syncEquipmentStandFromSource(sourcePlanogram, planogram));

		return siblings;
	}

	public finalizeSharedEquipmentMove(sourcePlanogram: IPlanogram, changedElementIds: Array<string> = []): void {
		sourcePlanogram.stand.blocks.forEach((block: Block) => block.clearCache());
		const normalizedElements: Array<Element> = this.normalizeElements(sourcePlanogram.stand);
		const elementIds: Array<string> = changedElementIds.length
			? changedElementIds
			: normalizedElements.map((el: Element) => el.id);
		const syncedPlanograms: Array<IPlanogram> = this.syncSharedEquipmentStands(sourcePlanogram);

		syncedPlanograms.forEach((planogram: IPlanogram) => {
			this.normalizeElements(planogram.stand);
			this.historyGenerate$.next(planogram.main_stand);
		});
		this.historyGenerate$.next(sourcePlanogram.main_stand);
		this.requestCanvasUpdate([sourcePlanogram, ...syncedPlanograms], elementIds);
	}

	public getPlanogramByStand(stand: EquipmentBase): IPlanogram | undefined {
		return this._allPlanograms().find((planogram: IPlanogram) => planogram.stand === stand);
	}

	public getPlanogramByPOSMaterial(material: POSMaterial): IPlanogram | undefined {
		return this._allPlanograms().find((planogram: IPlanogram) => planogram.stand.POSMaterials.includes(material));
	}

	public removePOSMaterialsFromStand(stand: EquipmentBase, materials: Array<POSMaterial>): void {
		const removeSet: Set<POSMaterial> = new Set(materials);

		stand.POSMaterials = stand.POSMaterials.filter((material: POSMaterial) => !removeSet.has(material));
	}

	public getPlanogramByBlock(block: Block): IPlanogram | undefined {
		return this._allPlanograms().find((planogram: IPlanogram) => planogram.stand.blocks.includes(block));
	}

	public removeBlockFromStandForTransfer(stand: EquipmentBase, block: Block): Array<Element> {
		let normalizeElements: Array<Element> = [];

		if (block.products.length) {
			normalizeElements = this.removeProductsFromStandForTransfer(stand, block.products);
		} else {
			normalizeElements = [...block.elements];
		}

		const blockIndex: number = stand.blocks.indexOf(block);

		if (blockIndex !== -1) {
			stand.blocks.splice(blockIndex, 1);
		}

		stand.blocks.forEach((b: Block) => b.clearCache());
		block.clearCache();

		return normalizeElements;
	}

	public resolveProductForPlanogram(planogram: IPlanogram, product: Product): Product {
		if (planogram.products().includes(product)) return product;

		const existing: Product | undefined = planogram.products().find((p: Product) => p.id === product.id);
		const resolved: Product = existing ?? product.copy();

		if (!existing) {
			planogram.products.set([...planogram.products(), resolved]);
		}

		if (product.image) {
			resolved.image = product.image;
		} else if (!resolved.image) {
			resolved.loadImage();
		}

		return resolved;
	}

	public ensureProductsInPlanogramCatalog(planogram: IPlanogram, productGroups: Array<ProductGroup>): void {
		productGroups.forEach((pg: ProductGroup) => {
			pg.product = this.resolveProductForPlanogram(planogram, pg.product);
		});
	}

	public removeProductsFromStandForTransfer(stand: EquipmentBase, productsToRemove: Array<ProductGroup>): Array<Element> {
		const removeSet: Set<ProductGroup> = new Set(productsToRemove);
		let dependedProducts: Array<ProductGroup> = [];

		productsToRemove.forEach((pg: ProductGroup) => {
			dependedProducts = [
				...dependedProducts,
				...Utils.findDependProducts(pg, stand.products)
			];
		});
		dependedProducts = dependedProducts.filter((pg: ProductGroup) => removeSet.has(pg));

		stand.products.forEach((pg: ProductGroup) => {
			if (removeSet.has(pg)) return;

			if (pg.dependProduct && removeSet.has(pg.dependProduct)) {
				dependedProducts = dependedProducts.filter((item: ProductGroup) => item !== pg);
				pg.dependProduct = pg.element.getDroppedProduct(
					pg.begin.x,
					pg.element.parent.type == 'AngleShelf' ? pg.getBoundingY().max : pg.begin.y,
					pg.begin.z,
					productsToRemove,
					pg
				);
				pg.begin.y = pg.dependProduct
					? pg.dependProduct.getBoundingY().max
					: pg.element.getBoundingY().max;
			}
		});

		stand.products
			.filter((pg: ProductGroup) => pg.dependProduct)
			.sort((a: ProductGroup, b: ProductGroup) => a.begin.y - b.begin.y)
			.forEach((pg: ProductGroup) => {
				if (dependedProducts.includes(pg)) {
					pg.begin.y = pg.dependProduct
						? pg.dependProduct.getBoundingY().max
						: pg.element.getBoundingY().max;
				}
			});

		const sourceElements: Array<Element> = Utils.unique(productsToRemove.map((pg: ProductGroup) => pg.element));
		stand.products = stand.products.filter((pg: ProductGroup) => !removeSet.has(pg));

		const changedBlocks: Array<Block> = stand.blocks
			.filter((block: Block) => block.products.some((pg: ProductGroup) => removeSet.has(pg)));

		changedBlocks.forEach((block: Block) => {
			const blockIndex: number = stand.blocks.indexOf(block);

			stand.blocks[blockIndex].updateBlockProducts(
				this.getDependBlockProducts(stand.blocks[blockIndex], stand)
			);
		});

		stand.blocks.forEach((block: Block) => block.clearCache());

		return sourceElements;
	}

	getHistoryState(planogramId: number): IPlanogramHistoryState {
		return this._histories()[planogramId] ?? EMPTY_HISTORY;
	}

	private setHistoryState(planogramId: number, state: IPlanogramHistoryState): void {
		this._histories.update((histories: Record<number, IPlanogramHistoryState>) => ({
			...histories,
			[planogramId]: state
		}));
	}

	private resolveHistoryId(planogramId?: number): number | null {
		return planogramId ?? this.activePlanogramId();
	}

	private clampSavedIndex(savedIndex: number, length: number): number {
		return savedIndex < 0 || savedIndex >= length ? -1 : savedIndex;
	}

	public pushHistory(instance: HistoryInstance, planogramId?: number): void {
		const id = this.resolveHistoryId(planogramId);
		if (!id) return;

		const current: IPlanogramHistoryState = this.getHistoryState(id);
		const entries: Array<HistoryInstance> = [...current.entries.slice(0, current.index + 1), instance];
		const trimmed: Array<HistoryInstance> = entries.slice(-MAX_HISTORY_ENTRIES);
		const overflow: number = entries.length - trimmed.length;
		const savedIndex: number = current.savedIndex > current.index
			? -1
			: this.clampSavedIndex(current.savedIndex - overflow, trimmed.length);

		this.setHistoryState(id, {
			...current,
			entries: trimmed,
			index: trimmed.length - 1,
			savedIndex
		});
	}

	public popLastHistoryEntry(planogramId?: number): void {
		const id = this.resolveHistoryId(planogramId);
		if (!id) return;

		const current: IPlanogramHistoryState = this.getHistoryState(id);
		if (!current.entries.length) return;

		const entries: Array<HistoryInstance> = current.entries.slice(0, -1);

		this.setHistoryState(id, {
			...current,
			entries,
			index: Math.min(current.index, Math.max(0, entries.length - 1)),
			savedIndex: this.clampSavedIndex(current.savedIndex, entries.length)
		});
	}

	public navigateHistory(delta: -1 | 1): HistoryInstance | null {
		const id: number = this.activePlanogramId();
		if (!id || this._historyApplying()) return null;

		const current: IPlanogramHistoryState = this.getHistoryState(id);
		const newIndex: number = current.index + delta;
		if (newIndex < 0 || newIndex >= current.entries.length) return null;

		this.setHistoryState(id, { ...current, index: newIndex });
		return current.entries[newIndex];
	}

	public clearHistory(planogramId?: number): void {
		const id = this.resolveHistoryId(planogramId);
		if (!id) return;

		this.setHistoryState(id, { ...EMPTY_HISTORY });
	}

	public setBeginState(state: HistoryInstance | null, planogramId?: number): void {
		const id = this.resolveHistoryId(planogramId);
		if (!id) return;

		this.setHistoryState(id, { ...this.getHistoryState(id), beginState: state });
	}

	public setBeginStateFromIndex(planogramId?: number): void {
		const id = this.resolveHistoryId(planogramId);
		if (!id) return;

		const current: IPlanogramHistoryState = this.getHistoryState(id);
		this.setHistoryState(id, { ...current, beginState: current.entries[current.index] ?? null });
	}

	public setBeginStateFromFirst(planogramId?: number): void {
		const id = this.resolveHistoryId(planogramId);
		if (!id) return;

		const current: IPlanogramHistoryState = this.getHistoryState(id);
		this.setHistoryState(id, { ...current, beginState: current.entries[0] ?? null });
	}

	public markHistorySaved(planogramId?: number): void {
		const id = this.resolveHistoryId(planogramId);
		if (!id) return;

		const current: IPlanogramHistoryState = this.getHistoryState(id);
		if (!current.entries.length) return;

		this.setHistoryState(id, {
			...current,
			savedIndex: current.index,
			beginState: current.entries[current.index]
		});
	}

	public updateHistoryEntries(
		updater: (entries: Array<HistoryInstance>) => Array<HistoryInstance>,
		planogramId?: number
	): void {
		const id = this.resolveHistoryId(planogramId);
		if (!id) return;

		const current: IPlanogramHistoryState = this.getHistoryState(id);
		const entries: Array<HistoryInstance> = updater([...current.entries]);

		this.setHistoryState(id, {
			...current,
			entries,
			index: Math.min(current.index, Math.max(0, entries.length - 1)),
			savedIndex: this.clampSavedIndex(current.savedIndex, entries.length)
		});
	}

	public forEachHistoryEntry(callback: (entry: HistoryInstance) => void, planogramId?: number): void {
		const id = this.resolveHistoryId(planogramId);
		if (!id) return;

		this.getHistoryState(id).entries.forEach(callback);
	}

	public setHistoryApplying(value: boolean): void {
		this._historyApplying.set(value);
	}

	public hasHistory(planogramId?: number): boolean {
		const id = this.resolveHistoryId(planogramId);
		return !!id && this.getHistoryState(id).entries.length > 0;
	}

	public hasHistoryChanges(planogramId: number): boolean {
		const state: IPlanogramHistoryState = this.getHistoryState(planogramId);
		return state.index !== state.savedIndex;
	}

	public addActiveBlock(block: Block): void {
		this._activeBlocks.update((blocks: Array<Block>) => [...blocks, block]);
	}

	public isBlockActive(block: Block): boolean {
		return this._activeBlocks().includes(block);
	}

	public setActiveBlocks(blocks: Array<Block>): void {
		this._activeBlocks.set(blocks);
	}

	public refreshActiveBlocks(): void {
		this._activeBlocks.update((blocks: Array<Block>) => [...blocks]);
	}

	public clearActiveBlocks(): void {
		this._activeBlocks.set([]);
	}

	public addActivePOSMaterial(material: POSMaterial): void {
		this._activePosMaterials.update((materials: Array<POSMaterial>) => [...materials, material]);
	}

	public removeActivePOSMaterial(material: POSMaterial): void {
		this._activePosMaterials.update((materials: Array<POSMaterial>) => materials.filter((m: POSMaterial) => m !== material));
	}

	public isPOSMaterialActive(material: POSMaterial): boolean {
		return this._activePosMaterials().includes(material);
	}

	public setActivePOSMaterials(materials: Array<POSMaterial>): void {
		this._activePosMaterials.set(materials);
	}

	public refreshPOSMaterials(): void {
		this._activePosMaterials.update((materials: Array<POSMaterial>) => [...materials]);
	}

	public clearPOSMaterials(): void {
		this._activePosMaterials.set([]);
	}

	public addActiveProduct(pg: ProductGroup): void {
		this._activeProducts.update((products: Array<ProductGroup>) => [...products, pg]);
	}

	public removeActiveProduct(pg: ProductGroup): void {
		this._activeProducts.update((products: Array<ProductGroup>) => products.filter((p: ProductGroup) => p != pg));
	}

	public isProductActive(pg: ProductGroup): boolean {
		return this._activeProducts().includes(pg);
	}

	public setActiveProducts(groups: Array<ProductGroup>): void {
		this._activeProducts.set(groups);
	}

	public refreshActiveProducts(): void {
		this._activeProducts.update((products: Array<ProductGroup>) => [...products]);
	}

	public clearActiveProducts(): void {
		this._activeProducts.set([]);
	}

	public clearPlanogramActiveProducts(stand: EquipmentBase): void {
		const remaining: Array<ProductGroup> = this._activeProducts()
			.filter((pg: ProductGroup) => pg.element.stand !== stand);

		if (!remaining.length) {
			this.setProductActive();
			return;
		}

		this.setActiveProducts(remaining);
		this.updateSettingsPanel({
			emptyProducts: false,
			differentElements: Utils.unique(remaining.map((pg: ProductGroup) => pg.element.constructor.name)).length > 1,
			looseProduct: remaining.some((p: ProductGroup) => p.isLooseForm) && remaining.some((p: ProductGroup) => !p.isLooseForm)
		});
	}

	public retainActiveProductsForStand(stand: EquipmentBase): void {
		const activeProducts: Array<ProductGroup> = this._activeProducts();
		const retained: Array<ProductGroup> = activeProducts.filter((pg: ProductGroup) => pg.element.stand === stand);

		if (retained.length === activeProducts.length) return;

		if (!retained.length) {
			this._activeProducts.set([]);
			this.updateSettingsPanel({
				emptyProducts: true,
				differentElements: false,
				looseProduct: false
			});

			if (this.settingsPanelDisabled()) this.closePanel();
			return;
		}

		this.setActiveProducts(retained);
		this.updateSettingsPanel({
			emptyProducts: false,
			differentElements: Utils.unique(retained.map((pg: ProductGroup) => pg.element.constructor.name)).length > 1,
			looseProduct: retained.some((p: ProductGroup) => p.isLooseForm) && retained.some((p: ProductGroup) => !p.isLooseForm)
		});
	}

	public clearPlanogramActiveBlocks(stand: EquipmentBase): void {
		const remaining: Array<Block> = this._activeBlocks().filter((block: Block) => block.stand !== stand);

		if (!remaining.length) {
			this.setActiveBlocks([]);
			this.updateSettingsPanel({ emptyBlocks: true });
			if (this.settingsPanelDisabled()) this.closePanel();
			return;
		}

		this.setActiveBlocks(remaining);
		this.updateSettingsPanel({ emptyBlocks: false });
	}

	public clearPlanogramActivePosMaterials(stand: EquipmentBase): void {
		const remaining: Array<POSMaterial> = this._activePosMaterials()
			.filter((material: POSMaterial) => !stand.POSMaterials.includes(material));

		if (!remaining.length) {
			this.setActivePOSMaterials([]);
			this.updateSettingsPanel({ emptyLabels: true, tooManyLabels: false });
			if (this.settingsPanelDisabled()) this.closePanel();
			return;
		}

		this.setActivePOSMaterials(remaining);
		this.updateSettingsPanel({
			emptyLabels: false,
			tooManyLabels: remaining.length > 1
		});
	}

	public clearPlanogramSelection(stand: EquipmentBase): void {
		this.clearPlanogramActiveProducts(stand);
		this.clearPlanogramActiveBlocks(stand);
		this.clearPlanogramActivePosMaterials(stand);

		if (this._selectedBlock()?.stand === stand) this.clearSelectedBlock();
	}

	public setProductsGrouping(value: string): void {
		this._productsGrouping.set(value);
	}

	private findCatalogProduct(product: Product): Product {
		for (const planogram of this._allPlanograms()) {
			const found: Product | undefined = planogram.products().find((p: Product) => p.id === product.id);

			if (found) return found;
		}

		return product;
	}

	private resolvePanelProduct(pg: ProductGroup): Product {
		if (this._productsGrouping() !== 'planograms') return this.findCatalogProduct(pg.product);

		const planogram: IPlanogram | undefined = this.getPlanogramByStand(pg.element.stand);

		return planogram ? this.resolveProductForPlanogram(planogram, pg.product) : pg.product;
	}

	public addActivePanelProduct(product: Product): void {
		const panelProduct: Product = this._productsGrouping() !== 'planograms' ? this.findCatalogProduct(product) : product;

		if (this.isPanelProductActive(panelProduct)) return;

		panelProduct.selected = true;
		this._activePanelProducts.update((products: Array<Product>) => [...products, panelProduct]);
	}

	public removeActivePanelProduct(product: Product): void {
		if (this._productsGrouping() !== 'planograms') {
			this._activePanelProducts()
				.filter((p: Product) => p.id === product.id)
				.forEach((p: Product) => p.selected = false);
			this._activePanelProducts.update((products: Array<Product>) => products.filter((p: Product) => p.id !== product.id));
			return;
		}

		product.selected = false;
		this._activePanelProducts.update((products: Array<Product>) => products.filter((p: Product) => p != product));
	}

	public isPanelProductActive(product: Product): boolean {
		return this._productsGrouping() !== 'planograms'
			? this._activePanelProducts().some((p: Product) => p.id === product.id)
			: this._activePanelProducts().includes(product);
	}

	public setActivePanelProducts(products: Array<Product>): void {
		products.forEach((product: Product) => product.selected = true);
		this._activePanelProducts.set(products);
	}

	public refreshActivePanelProducts(): void {
		this._activePanelProducts.update((products: Array<Product>) => [...products]);
	}

	public clearActivePanelProducts(): void {
		this._activePanelProducts().forEach((product: Product) => product.selected = false);
		this._activePanelProducts.set([]);
	}

	public setDragProducts(products: Array<Product>): void {
		this._dragProducts.set(products);
	}

	public clearDragProducts(): void {
		this._dragProducts.set([]);
	}

	public setEditMode(value: boolean): void {
		this._editMode.set(value);
	}

	public setFilters(value: { interval: number, metrics: Array<string> }): void {
		this._filters.set(value);
	}

	public setFilterType(type: string): void {
		this._filterType.set(type);
	}

	public setFocusedCanvas(planogram: number, element: string, canvas: HTMLDivElement): void {
		this._focusedCanvas.set({ planogram: planogram, element: element, canvas: canvas });
	}

	public isCanvasFocused(canvas: HTMLDivElement): boolean {
		return this._focusedCanvas().canvas == canvas;
	}

	public setHighlightValue(value: HighlightOptionsValue): void {
		this._highlightValue.set(value);
	}

	public clearHighlightValue(): void {
		this._highlightValue.set(HighlightOptionsValue.Empty);
	}

	public setHighlightedProducts(value: any): void {
		this._highlightedProducts.set(value);
	}

	public isProductHighlighted(id: number): boolean {
		return this._highlightedProducts().hasOwnProperty(id);
	}

	public clearHighlightedProducts(): void {
		this._highlightedProducts.set({});
	}

	public updateLabelsState(values: Partial<ILabelsState>): void {
		this._labelsState.update((state: ILabelsState) => ({ ...state, ...values }));
	}

	public setLayoutRulesProducts(value: any): void {
		this._layoutRulesProducts.set(value);
	}

	public isProductInRule(id: number): boolean {
		return this._layoutRulesProducts().hasOwnProperty(id);
	}

	public clearLayoutRulesProducts(): void {
		this._layoutRulesProducts.set({});
	}

	public openPanel(type: string): void {
		this._panelType.set(type);
	}

	public closePanel(): void {
		this._panelType.set('');
	}

	public setProductMove(state: boolean): void {
		this._productMove.set(state);
	}

	/*public startProductMove(): void {
		this._productMove.set(true);
	}

	public stopProductMove(): void {
		this._productMove.set(false);
	}*/

	public addRotatedProduct(id: number): void {
		this._rotatedProducts.update((products) => ({ ...products, [id]: { color: 'green' } }));
	}

	public isProductRotated(id: number): boolean {
		return this._rotatedProducts().hasOwnProperty(id);
	}

	public setSelectedElement(value: string): void {
		if (this._selectedElement() === value) return;

		this._selectedElement.set(value);
		this.clearEquipmentDistanceLines();
	}

	public clearSelectedElement(): void {
		if (!this._selectedElement()) return;

		this._selectedElement.set('');
		this.clearEquipmentDistanceLines();
	}

	public shouldShowEquipmentDistanceLines(planogramMainStand: number): boolean {
		const focusedPlanogramId: number | null = this._focusedCanvas().planogram;

		return focusedPlanogramId != null && focusedPlanogramId === planogramMainStand;
	}

	public setSelectedBlock(block: Block): void {
		this._selectedBlock.set(block);
	}

	public clearSelectedBlock(): void {
		this._selectedBlock.set(null);
	}

	public updateSettingsPanel(values: Partial<ISettingsPanel>): void {
		this._settingsPanel.update((settings: ISettingsPanel) => ({ ...settings, ...values }));
	}

	public getStandData(id: number): any {
		return this._standData()[id] ?? null;
	}

	public setStandData(data: any) {
		this._standData.set(data);
	}

	public updateStandData(id: number, data: any) {
		this._standData.update((items: any) => ({ ...items, [id]: data }));
	}

	public clearStandData(): void {
		this._standData.set({});
	}

	public setTargetElement(target: Element): void {
		const previous: Element | null = this._targetElement();
		if (previous === target) return;

		this._targetElement.set(target);
		this.clearEquipmentDistanceLines();
	}

	public canChangeProductPositionFor(activeProducts: Array<ProductGroup>): boolean {
		return activeProducts.length === 1
			&& activeProducts[0].element.parent.is(...CAN_CHANGE_PRODUCT_POSITION)
			&& !activeProducts[0].isLooseForm;
	}

	public isSortingActiveFor(planogram: IPlanogram, activeProducts: Array<ProductGroup>, activeBlocks: Array<Block>): boolean {
		if (activeProducts.length > 1) {
			const groupedByElement = Utils.groupBy(activeProducts, 'element');

			if (groupedByElement.length > 1) return false;
			if (groupedByElement[0][0].element.parent.is(EquipmentElements.Hook, EquipmentElements.Hole)) return false;

			const maxX: number = Math.max(...activeProducts.map((pg: ProductGroup) => pg.getBoundingX().max));
			const minX: number = Math.min(...activeProducts.map((pg: ProductGroup) => pg.begin.x));

			return !planogram.stand.products
				.filter((pg: ProductGroup) => pg.element === groupedByElement[0][0].element)
				.some((pg: ProductGroup) => pg.begin.x < maxX && pg.begin.x > minX && !activeProducts.includes(pg));
		}

		if (activeBlocks.length) {
			return activeBlocks[0].elements.some((el: Element) => el.parent.is(EquipmentElements.Hook, EquipmentElements.Hole));
		}

		return false;
	}

	public clearTargetElement(): void {
		if (!this._targetElement()) return;

		this._targetElement.set(null);
		this.clearEquipmentDistanceLines();
	}

	public clearEquipmentDistanceLines(): void {
		if (!this._allPlanograms().length) return;

		this.requestCanvasUpdate(
			[...this._allPlanograms()],
			[],
			undefined,
			[PlanogramCanvasLayer.Tools]
		);
	}

	public requestCanvasUpdate(
		stands: Array<IPlanogram> = [],
		ids: Array<string> = [],
		reason?: string,
		layers?: Array<PlanogramCanvasLayer>
	): Observable<CanvasUpdateRequest> {
		const requestId: string = `${Date.now()}-${Math.random()}`;
		const payload: CanvasUpdateRequest = { stands, ids, reason, requestId, layers };

		this.canvasUpdate$.next(payload);

		return this.canvasUpdateCompleted$.pipe(
			filter((event: CanvasUpdateRequest) => event.requestId === requestId),
			take(1)
		);
	}

	public syncProducts(pg: ProductGroup, event: MouseEvent): void {
		const product: Product = this.resolvePanelProduct(pg);
		const multi: boolean = navigator.platform == 'MacIntel' ? event?.metaKey : event?.ctrlKey;

		if (multi) {
			if (!this.isPanelProductActive(product)) {
				this.addActivePanelProduct(product);
			} else if (this.activePanelProducts().length > 1) {
				this.removeActivePanelProduct(product);
			}
		} else {
			this.clearActivePanelProducts();
			this.addActivePanelProduct(product);
		}
	}

	public syncPanelFromCanvas(): void {
		const activeProducts: Array<ProductGroup> = this._activeProducts();

		if (!activeProducts.length) {
			this.clearActivePanelProducts();
			return;
		}

		const panelProducts: Array<Product> = [];
		const seen: Set<number | Product> = new Set();

		activeProducts.forEach((pg: ProductGroup) => {
			const product: Product = this.resolvePanelProduct(pg);
			const key: number | Product = this._productsGrouping() !== 'planograms' ? product.id : product;

			if (seen.has(key)) return;

			seen.add(key);
			panelProducts.push(product);
		});

		this.setActivePanelProducts(panelProducts);
	}

	public setProductActive(product: ProductGroup = null, multi: boolean = false, checkInactivity: boolean = true): void {
		if (this._activeBlocks().length) this.setBlockActive(null, false);
		if (this._activePosMaterials().length) this.setPOSMaterialActive();

		if (!product || !multi) this.clearActiveProducts();

		if (product) {
			if (!this.isProductActive(product)) {
				this.addActiveProduct(product);
			} else if (this._activeProducts().length > 1) {
				this.removeActiveProduct(product);
			}
		}

		this.updateSettingsPanel({
			emptyProducts: !this._activeProducts().length,
			differentElements: Utils.unique(this._activeProducts().map((pg) => pg.element.constructor.name)).length > 1,
			looseProduct: this._activeProducts().some((p: ProductGroup) => p.isLooseForm) && this._activeProducts().some((p: ProductGroup) => !p.isLooseForm)
		});

		if (checkInactivity && this.settingsPanelDisabled()) this.closePanel();
	}

	public setBlockActive(element: Block = null, checkInactivity: boolean = true) {
		if (this.activeProducts().length) this.setProductActive(null, false, false);
		if (this.activePosMaterials().length) this.setPOSMaterialActive();
		if (!element && this.activeBlocks().length) this.closePanel();
		this.setActiveBlocks(element ? [element] : []);
		this.updateSettingsPanel({ emptyBlocks: !this.activeBlocks().length });
		if (checkInactivity && this.settingsPanelDisabled()) this.closePanel();
	}

	public setPOSMaterialActive(material: POSMaterial = null, multi: boolean = false): void {
		if (this.activeBlocks().length) this.setBlockActive();
		if (this.activeProducts().length) this.setProductActive();

		if (!material) {
			this.clearPOSMaterials();
			this.updateSettingsPanel({ emptyLabels: true, tooManyLabels: false });
			return;
		}

		if (multi) {
			if (this.isPOSMaterialActive(material)) {
				this.removeActivePOSMaterial(material);
			} else {
				this.addActivePOSMaterial(material);
			}
		} else {
			this.setActivePOSMaterials([material]);
		}

		this.updateSettingsPanel({
			emptyLabels: !this.activePosMaterials().length,
			tooManyLabels: this.activePosMaterials().length > 1
		});

		if (this.settingsPanelDisabled()) this.closePanel();
	}

	public copyPOSMaterial(planogram: IPlanogram): void {
		this.setPlanogram(planogram);

		const activePosMaterials: Array<POSMaterial> = this.activePosMaterials()
			.filter((material: POSMaterial) => planogram.stand.POSMaterials.includes(material));

		if (activePosMaterials.length !== 1) return;

		const material: POSMaterial = activePosMaterials[0].copy();
		material.y += 0.1;
		planogram.stand.POSMaterials.push(material);
		this.requestCanvasUpdate([planogram], [], undefined, [PlanogramCanvasLayer.PosMaterials])
			.subscribe(() => this.setPOSMaterialActive(material));
		this.historyGenerate$.next(planogram.main_stand);
	}

	async deleteItems(planogram?: IPlanogram): Promise<void> {
		const stands: Array<IPlanogram> = planogram ? [planogram] : this._allPlanograms();
		if (!stands.length) return;

		const activeProducts: Array<ProductGroup> = planogram
			? this.activeProducts().filter((pg: ProductGroup) => pg.element.stand === planogram.stand)
			: this.activeProducts();
		const activePosMaterials: Array<POSMaterial> = planogram
			? this.activePosMaterials().filter((material: POSMaterial) =>
					planogram.stand.POSMaterials.includes(material)
				)
			: this.activePosMaterials();

		let dependedProducts: Array<ProductGroup> = [], elements: Array<Element> = [], removedBlocks: Array<Block> = [];
		activeProducts.forEach(
			(el: ProductGroup) =>
				(dependedProducts = [
					...dependedProducts,
					...Utils.findDependProducts(el, el.element.stand.products)
				])
		);

		stands.forEach((stand: IPlanogram) => {
			stand.stand.products = stand.stand.products.filter((p: ProductGroup) => !this.isProductActive(p));
			stand.stand.products.forEach((p: ProductGroup) => {
				if (this.isProductActive(p.dependProduct)) {
					dependedProducts = dependedProducts
						.filter((pg: ProductGroup) => pg !== p)
						.filter((v: ProductGroup, i: number, a: Array<ProductGroup>) => a.indexOf(v) === i);

					p.dependProduct = p.element.getDroppedProduct(
						p.begin.x,
						p.element.parent.type == 'AngleShelf'
							? p.getBoundingY().max
							: p.begin.y,
						p.begin.z,
						activeProducts,
						p
					);
					p.begin.y = p.dependProduct
						? p.dependProduct.getBoundingY().max
						: p.element.getBoundingY().max;
				}
			});
			stand.stand.products
				.filter((p: ProductGroup) => p.dependProduct)
				.sort((a: ProductGroup, b: ProductGroup) => a.begin.y - b.begin.y)
				.forEach((p: ProductGroup) => {
					if (dependedProducts.includes(p)) {
						p.begin.y = p.dependProduct
							? p.dependProduct.getBoundingY().max
							: p.element.getBoundingY().max;
					}
				});

			const standRemovedBlocks: Array<Block> = stand.stand.blocks.filter((b: Block) => this.isBlockActive(b));
			removedBlocks = [...removedBlocks, ...standRemovedBlocks];

			if (stand.stand.blocks.some((b: Block) => b.products) && standRemovedBlocks.length) {
				stands.forEach((innerStand: IPlanogram) => {
					innerStand.stand.products = innerStand.stand.products.filter(
						(p: ProductGroup) => !standRemovedBlocks[0].products.includes(p)
					);
					innerStand.stand.products.forEach((p: ProductGroup) => {
						if (standRemovedBlocks[0].products.includes(p.dependProduct)) {
							dependedProducts = dependedProducts.filter((pg: ProductGroup) => pg !== p);

							p.dependProduct = p.element.getDroppedProduct(
								p.begin.x,
								p.begin.y,
								p.begin.z,
								activeProducts,
								p
							);
							p.begin.y = p.dependProduct
								? p.dependProduct.getBoundingY().max
								: p.element.getBoundingY().max;
						}
					});

					innerStand.stand.products.forEach((p: ProductGroup) => {
						if (dependedProducts.includes(p)) {
							p.begin.y = p.dependProduct
								? p.dependProduct.getBoundingY().max
								: p.element.getBoundingY().max;
						}
					});
				});
			}

			stand.stand.blocks = stand.stand.blocks.filter((b: Block) => !standRemovedBlocks.includes(b));

			const elementsToNormalize: Array<Element> = [];
			activeProducts.forEach((pg: ProductGroup) => {
				if (pg.element.stand === stand.stand && !elementsToNormalize.includes(pg.element)) {
					elementsToNormalize.push(pg.element);
				}
			});
			removedBlocks.forEach((block: Block) => {
				if (block.stand === stand.stand) {
					block.elements.forEach((el: Element) => {
						if (!elementsToNormalize.includes(el)) elementsToNormalize.push(el);
					});
				}
			});
			elements.push(...this.normalizeElements(stand.stand, elementsToNormalize));
			this.setNumbersForPerforation$.next(elementsToNormalize);
		});

		if (activePosMaterials.length) {
			stands.forEach((stand: IPlanogram) => {
				stand.stand.POSMaterials = stand.stand.POSMaterials
					.filter((material: POSMaterial) => !activePosMaterials.includes(material));
			});
			if (activePosMaterials.some((material: POSMaterial) => material instanceof POSImageMaterial)) {
				this.mixPanelService.trackPage(MixpanelEvents.POSDeleteFromPlanogram);
			}
			if (activePosMaterials.some((material: POSMaterial) => material instanceof POSTextMaterial)) {
				this.mixPanelService.trackPage(MixpanelEvents.POSTextDelete);
			}
			this.clearPOSMaterials();
		}

		if (removedBlocks.includes(this.selectedBlock())) {
			this.clearSelectedBlock();
		}
		if (removedBlocks.length) {
			const currentPlanogram: IPlanogram = this._planogram();
			if (currentPlanogram) this.planogramUpdate$.next(currentPlanogram);
		}
		this.setBlockActive();
		this.closePanel();
		this.clearActivePanelProducts();
		this.setRealProductPercents$.next();
		const elementIds: Array<string> = removedBlocks.length
			? []
			: [...new Set(elements.map((el: Element) => el.id))];
		this.requestCanvasUpdate(stands, elementIds).subscribe({
			complete: () => {
				this.setProductActive();
			}
		});
		stands.forEach((stand: IPlanogram) => this.historyGenerate$.next(stand.main_stand));
	}

	public normalizeElements(stand: EquipmentBase, elements: Array<Element> = [], segments: boolean = false): Array<Element> {
		const alreadyNormalized: Array<Element> = [];
		if (!elements.length || this.highlightValue() === HighlightOptionsValue.Doubles) elements = stand.allElements;
		elements.forEach((el: Element) => {
			if (NORMALIZED_ELEMENTS.includes(el.constructor.name) && alreadyNormalized.every((n) => n != el)) {
				el.normalize(stand, segments ? (this.blockForRule().id ? el.getBlockSegments : el.getBlockNotEmptySegments) : []);
				alreadyNormalized.push(el, ...el.getSiblingElements);
			}
		});
		stand.products
			.filter((pg: ProductGroup) => alreadyNormalized.includes(pg.element))
			.forEach((pg: ProductGroup) => delete pg.block);
		stand.blocks
			.sort((a: Block, b: Block) => {
				if (stand.alignment === 'right') {
					return b.boundingX.min - a.boundingX.min;
				} else {
					return a.boundingX.max - b.boundingX.max;
				}
			})
			.forEach((block: Block, idx: number) => {
				const otherBlockProducts = Utils.to1DArray(
						stand.blocks.filter((b: Block, i: number) => i < idx).map((b: Block) => b.products)
					),
					blockProductErrors = block.setProducts(otherBlockProducts);

				if (blockProductErrors.length) {
					this.showDeleteMessage$.next({ block: block, errors: blockProductErrors });
				}
			});

		return alreadyNormalized;
	}

	public changeProductPosition(pg: ProductGroup): void {
		if (pg.element.is(HangBarElement) || pg.isLooseForm) return;

		//this.changeCanvasListening(false);
		const dependedProduct: ProductGroup = this.planogram().stand.products
			.find((p: ProductGroup) => p.dependProduct == pg);

		pg.rotate();

		const pIdx: number = this.planogram().stand.products.indexOf(pg),
			aIdx: number = this.activeProducts().indexOf(pg),
			pgCopy: ProductGroup = pg.copy();

		this.planogram().stand.products[pIdx] = pgCopy;
		this._activeProducts.update((products: Array<ProductGroup>) => {
            const nextProducts: Array<ProductGroup> = [...products];
            nextProducts[aIdx] = pgCopy;
            return nextProducts;
        });

		if (dependedProduct) dependedProduct.dependProduct = pgCopy;

		let dependedProducts: Array<ProductGroup> = Utils.findDependProducts(pg, this.planogram().stand.products);

		this.planogram().stand.products
			.sort((a: ProductGroup, b: ProductGroup) => a.getBoundingY().min - b.getBoundingY().min)
			.forEach((p: ProductGroup) => {
				if (dependedProducts.includes(p)) {
					p.begin.y = p.dependProduct ? p.dependProduct.getBoundingY().max : p.element.getBoundingY().max;
				}
			});

		const groupToShift: Array<Array<ProductGroup>> = pgCopy.element.products
			.filter((group: Array<ProductGroup>) => group.some((p: ProductGroup) => pgCopy == p));

		pgCopy.element.shiftProductGroups(groupToShift, Utils.to1DArray(groupToShift));

		const elements: Array<Element> = this.normalizeElements(this.planogram().stand, [pgCopy.element]);

		this.planogram().stand.blocks.forEach((b: Block) => {
			const blockProductErrors: Array<ProductGroup> = b.setProducts(
				Utils.to1DArray(
					this.planogram().stand.blocks
						.filter((block: Block) => block != b)
						.map((block: Block) => block.products)
				)
			);

			if (blockProductErrors.length) this.showDeleteMessage$.next({ block: b, errors: blockProductErrors });
		});

		/*this.urgentUpdateCanvas([this.planogram], elements.map((el: Element) => el.id))
			.finally(() => {
				this.stopProductMove(true);
			});*/
		this.requestCanvasUpdate([], elements.map((el: Element) => el.id));
		this.historyGenerate$.next();
	}

	public getDependBlockProducts(block: Block, stand: EquipmentBase = this.planogram()?.stand) {
		if (!stand) return [...block.products];

		let dependProducts = [];
		const groupedProducts = Utils.groupByChildProperty(block.products, 'begin', 'x');
		groupedProducts.forEach((g) => {
			g.sort((a, b) => a.getBoundingY().min - b.getBoundingY().min);
			dependProducts.push(
				...stand.products.filter(
					(p) => p.begin.x == g[0].begin.x && p.element == g[0].element
				)
			);
		});

		return [...block.products, ...dependProducts.filter((p) => !block.products.includes(p))];
	}

	public sortSelectedProductGroups(planogram: IPlanogram, option: SortProductsOption): void {
		if (planogram.stand.arbitraryLayout) return;

		const activeProducts: Array<ProductGroup> = this.activeProducts()
			.filter((pg: ProductGroup) => pg.element.stand === planogram.stand);
		const activeBlocks: Array<Block> = this.activeBlocks()
			.filter((block: Block) => block.stand === planogram.stand);
		const elementsToNormalize: Array<Element> = [];
		let productsToSort: Array<ProductGroup>;

		if (activeProducts.length) {
			productsToSort = activeProducts;
		} else if (activeBlocks.length) {
			activeBlocks[0].updateBlockProducts(this.getDependBlockProducts(activeBlocks[0], planogram.stand));
			productsToSort = activeBlocks[0].products;
		} else {
			return;
		}

		productsToSort.forEach((pg: ProductGroup) => {
			if (!elementsToNormalize.includes(pg.element)) {
				elementsToNormalize.push(pg.element);
			}
		});

		const products: Array<Array<ProductGroup>> = elementsToNormalize
			.reduce((groups: Array<Array<ProductGroup>>, el: Element) => {
				return [
					...groups,
					...el.products.filter((group: Array<ProductGroup>) =>
						group.some((pg: ProductGroup) => productsToSort.includes(pg))
					)
				];
			}, [])
			.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => a[0].begin.x - b[0].begin.x);

		const sortedProducts: Array<ProductGroup> = products.map(
				(group: Array<ProductGroup>) =>
					group.sort(
						(a: ProductGroup, b: ProductGroup) => b.begin.z - a.begin.z || a.begin.y - b.begin.y
					)[0]
			),
			prevPositions: Array<number> = sortedProducts.map((pg: ProductGroup) => pg.begin.x);

		sortedProducts.sort(Utils.sortBy(option.by, option.type));
		sortedProducts.forEach((pg: ProductGroup, idx: number) => (pg.begin.x = prevPositions[idx]));
		sortedProducts.reduce(
			(a: ProductGroup | number, b: ProductGroup) => this.zipSortedProducts(a, b),
			prevPositions[0]
		);

		products.forEach((group: Array<ProductGroup>) =>
			group.forEach((pg: ProductGroup) => (pg.begin.x = group[0].begin.x))
		);

		this.normalizeElements(planogram.stand, elementsToNormalize);
		this.historyGenerate$.next(planogram.main_stand);
		this.requestCanvasUpdate([planogram]);
	}

	private zipSortedProducts(a: ProductGroup | number, b: ProductGroup): ProductGroup | number {
		if (typeof a === 'object') {
			b.begin.x = a.begin.x + a.Width;
			return b;
		}

		b.begin.x = a;
		return b.begin.x + b.Width;
	}

	public getDetailedVariables(detailedElement: Element): DetailedVariables {
		const basesList: Array<EquipmentElements> = [EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze];
		const shelvesList: Array<EquipmentElements> = [EquipmentElements.Shelf, EquipmentElements.AngleShelf];
		const backWallsList: Array<EquipmentElements> = [
			EquipmentElements.BackWall,
			EquipmentElements.CentralPillar,
			EquipmentElements.HookWall,
			EquipmentElements.PerforatedWall,
			EquipmentElements.Showcase
		];

		if (detailedElement.parent.is(...basesList, ...shelvesList)) {
			const sortedShelves: Array<Element> = [];

			if (detailedElement.parent.is(...basesList)) {
				const backWall: Element = detailedElement.stand.allElements.find((el: Element) => {
					return backWallsList.includes(el.parent.type) && el.depend.id === detailedElement.id;
				});

				sortedShelves.push(
					detailedElement,
					...detailedElement.stand.allElements
						.filter((el: Element) => shelvesList.includes(el.parent.type) && el.depend?.id === backWall?.id)
						.sort((a: Element, b: Element) => {
							const ySort: number = Utils.round(a.shapes[0].begin.y) - Utils.round(b.shapes[0].begin.y);
							const xSort: number = Utils.round(a.shapes[0].begin.x) - Utils.round(b.shapes[0].begin.x);

							return ySort || xSort;
						})
				);
			}

			if (detailedElement.parent.is(...shelvesList)) {
				const backWall: Element = detailedElement.depend;
				const base: Element = backWall.depend;

				sortedShelves.push(
					base,
					...detailedElement.stand.allElements
						.filter((el: Element) => shelvesList.includes(el.parent.type) && el.depend.id === backWall.id)
						.sort((a: Element, b: Element) => {
							const ySort: number = Utils.round(a.shapes[0].begin.y) - Utils.round(b.shapes[0].begin.y);
							const xSort: number = Utils.round(a.shapes[0].begin.x) - Utils.round(b.shapes[0].begin.x);

							return ySort || xSort;
						})
				);
			}

			const availableShelves: Array<Element> = sortedShelves.filter((el: Element) => {
				return !el.hasExtension(ExtensionType.Availability) || el.isExtensionActive(ExtensionType.Availability);
			});

			const currentIndex: number = sortedShelves.findIndex((el: Element) => el === detailedElement);

			return {
				detailedElementNumber: availableShelves.findIndex((el: Element) => el === detailedElement) + 1,
				detailedElementNext: currentIndex + 1 < sortedShelves.length ? sortedShelves[currentIndex + 1] : null,
				detailedElementPrev: currentIndex - 1 >= 0 ? sortedShelves[currentIndex - 1] : null
			};
		}

		if (detailedElement.parent.is(EquipmentElements.Hook)) {
			const parent: Element = detailedElement.depend;
			const hooks: Array<Element> = detailedElement.stand.allElements
				.filter((el: Element) => el.depend?.id === parent.id)
				.sort((a: Element, b: Element) => {
					const ySort: number = Utils.round(b.shapes[0].begin.y) - Utils.round(a.shapes[0].begin.y);
					const xSort: number = Utils.round(a.shapes[0].begin.x) - Utils.round(b.shapes[0].begin.x);

					return ySort || xSort;
				});

			const availableHooks: Array<Element> = hooks.filter((el: Element) => {
				return !el.hasExtension(ExtensionType.Availability) || el.isExtensionActive(ExtensionType.Availability);
			});

			const currentIndex: number = hooks.findIndex((el: Element) => el === detailedElement);

			return {
				detailedElementNumber: availableHooks.findIndex((el: Element) => el === detailedElement) + 1,
				detailedElementNext: currentIndex + 1 < hooks.length ? hooks[currentIndex + 1] : null,
				detailedElementPrev: currentIndex - 1 >= 0 ? hooks[currentIndex - 1] : null
			}
		}

		return { detailedElementNumber: 0, detailedElementNext: null, detailedElementPrev: null };
	}

	public dropProduct(planogram: IPlanogram, behavior: PlanogramViewBehavior, event: DndDropEvent): void {
		if (!this.dragProducts().length) return;

		this._dragProducts.set(this.dragProducts().map((product: Product) =>
			this.resolveProductForPlanogram(planogram, product)
		));

		this.clearActivePanelProducts();
		const defaultElement: Element = new ShelfElement(planogram.stand.bases[0]); // TODO replace with singleton NullElement
		defaultElement.stand = planogram.stand;
		const newProductGroups: Array<ProductGroup> = this.dragProducts().map((product: Product) => {
			const selectPacking = product.is_loose && product.packings?.length ? product.packings[0].id : null;
			return new ProductGroup({
				element: defaultElement,
				product: product,
				begin: { x: 0, y: 0, z: 0 },
				qty: 1,
				selectPacking: selectPacking,
				detailedRotationPoint: this.sessionService.personalSettings.productRotationPoint
			});
		});
		const point: Point = behavior.convertCanvasPointToPlanogram({ x: event.event.offsetX, y: event.event.offsetY });
		const element: Element = behavior.getAnchorElement(newProductGroups[0], point);

		if (!element) return;

		newProductGroups.forEach((pg: ProductGroup) => pg.angle = behavior.getProductAngle(element, point));

		point.z = behavior.getCorrectZCoordinate(element, point.z, newProductGroups[0]);
		const result: ICanPutProductsResult = element.canPutProductGroups(newProductGroups, point);
		if (!result.success) {
			if (result.message) this.snackBarService.setSnackBarNotification(result.message);
			return;
		}

		const normalizeElements: Array<Element> = [element];

		// set different x point for plane elements
		if (!element.parent.is(EquipmentElements.Hook, EquipmentElements.Hole)) {
			newProductGroups.forEach((pg: ProductGroup, index: number) => {
				pg.begin.x = index ? newProductGroups[index - 1].getBoundingX().max : point.x;
			});
		}

		newProductGroups.forEach((pg: ProductGroup, index: number) => {
			if (element.is(BoxElement) && pg.product.is_loose) pg.selectPacking = null;

			element.putProductGroups([pg], newProductGroups.map((pg: ProductGroup) => [pg]), { ...point, x: pg.begin.x });

			// set placement depth according to the personal settings
			if (behavior.isDetailedBehavior || pg.element.is(BoxElement, HookElement, HoleElement)) {
				if (!behavior.isDetailedBehavior && pg.element.is(HookElement)) {
					const personalSettingsDepth: number = this.sessionService.personalSettings.productDepthHook;
					const maxAvailableCount: number = Math.floor(Utils.round(pg.zMax / pg.ItemDepth)) || 1;

					if (!!~personalSettingsDepth) {
						pg.placementDepth = Math.min(personalSettingsDepth, maxAvailableCount);
					} else {
						pg.placementDepth = maxAvailableCount;
					}
				} else {
					pg.placementDepth = 1;
				}
			} else if (planogram.stand.arbitraryLayout) {
				pg.placementDepth = 1;
			} else {
				const personalSettingsDepth: number = this.sessionService.personalSettings.productDepthShelf,
					maxAvailableCount: number = Math.floor(Utils.round(pg.zMax / pg.ItemDepth)) || 1;

				if (!!~personalSettingsDepth) {
					pg.placementDepth = Math.min(personalSettingsDepth, maxAvailableCount);
				} else {
					pg.placementDepth = maxAvailableCount;
				}
			}

			if (pg.dependProduct) {
				pg.placementDepth = Math.floor(Utils.round(pg.dependProduct.Depth / pg.ItemDepth)) || 1;
			}

			pg.qty = pg.placementDepth;

			// mark products as selected on left panel
			if (!this.isPanelProductActive(pg.product)) this.addActivePanelProduct(pg.product);

			this.setProductActive(pg, index > 0);
		});

		let block: Block = planogram.stand.findBlock(point.x, point.y, newProductGroups[0].Width);

		if (this.selectedBlock() != block) this.setSelectedBlock(block);

		normalizeElements.push(...newProductGroups.map((pg: ProductGroup) => pg.element));
		planogram.stand.shiftProducts(newProductGroups.map((pg: ProductGroup) => [pg]), behavior.isDetailedBehavior);

		const elements: Array<Element> = this.normalizeElements(planogram.stand, normalizeElements);
		this.setNumbersForPerforation$.next(elements);

		this.clearDragProducts();

		this.reloadEfficiencyAnalysis$.next();
		this.reloadABCReport$.next();
		this.requestCanvasUpdate([planogram], elements.map((el: Element) => el.id));
		this.historyGenerate$.next();
		this.buildProductsTable$.next(false);
	}

	public moveProducts(planogram: IPlanogram, behavior: PlanogramViewBehavior, horizontal: number, vertical: number): void {
		if (!this.activeProducts().length) return;

		if (planogram.stand.alignment != AlignmentTypes.Default) return;

		const movementPerformed: boolean = behavior.moveProducts(this.activeProducts(), horizontal, vertical);

		if (movementPerformed) {
			const normalizedElements: Array<Element> = this.normalizeElements(
				planogram.stand,
				Utils.unique(this.activeProducts().map((pg: ProductGroup) => pg.element))
			);

			this.historyGenerate$.next();
			this.requestCanvasUpdate([planogram], normalizedElements.map((el: Element) => el.id));
		}
	}

	public movePOSMaterials(planogram: IPlanogram, behavior: PlanogramViewBehavior, horizontal: number, vertical: number): void {
		if (!this.activePosMaterials().length) return;

		if (planogram.stand.alignment != AlignmentTypes.Default) return;

		const movementPerformed: boolean = behavior.movePOSMaterials(this.activePosMaterials(), horizontal, vertical);

		if (movementPerformed) {
			this.historyGenerate$.next();
			this.requestCanvasUpdate([planogram], [], 'MOVE_POS_MATERIALS', [PlanogramCanvasLayer.PosMaterials]);
		}
	}
}